(() => {
  const minimumCompletionTime = 2000;
  const holdDuration = 1400;

  const hideMessage = (form) => {
    const message = form.querySelector('[data-so-antibot-message]');
    if (message) message.hidden = true;
  };

  const prepareHoldChallenge = (form) => {
    const challenge = form.querySelector('[data-so-antibot-challenge]');
    const button = form.querySelector('[data-so-antibot-hold]');
    if (!challenge || !button || challenge.dataset.soAntibotPrepared === 'true') return;

    const label = challenge.querySelector('[data-so-antibot-label]');
    const icon = challenge.querySelector('[data-so-antibot-icon]');
    let animationFrame;
    let startedAt = 0;

    const setProgress = (value) => {
      const clamped = Math.max(0, Math.min(1, value));
      challenge.style.setProperty('--so-human-progress', `${clamped * 100}%`);
    };

    const cancelHold = () => {
      if (challenge.dataset.soAntibotVerified === 'true') return;
      cancelAnimationFrame(animationFrame);
      animationFrame = undefined;
      startedAt = 0;
      challenge.classList.remove('is-holding');
      setProgress(0);
    };

    const completeHold = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = undefined;
      challenge.dataset.soAntibotVerified = 'true';
      challenge.classList.remove('is-holding', 'is-error');
      challenge.classList.add('is-verified');
      button.setAttribute('aria-pressed', 'true');
      button.disabled = true;
      setProgress(1);
      if (label) label.textContent = 'Verified — thank you';
      if (icon) icon.textContent = '✓';
      hideMessage(form);
    };

    const updateHold = (timestamp) => {
      if (!startedAt) startedAt = timestamp;
      const progress = (timestamp - startedAt) / holdDuration;
      setProgress(progress);
      if (progress >= 1) {
        completeHold();
        return;
      }
      animationFrame = requestAnimationFrame(updateHold);
    };

    const startHold = (event) => {
      if (challenge.dataset.soAntibotVerified === 'true' || event.isTrusted === false) return;
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      cancelAnimationFrame(animationFrame);
      challenge.classList.remove('is-error');
      challenge.classList.add('is-holding');
      startedAt = 0;
      animationFrame = requestAnimationFrame(updateHold);
    };

    button.addEventListener('pointerdown', startHold);
    button.addEventListener('pointerup', cancelHold);
    button.addEventListener('pointercancel', cancelHold);
    button.addEventListener('pointerleave', cancelHold);
    button.addEventListener('keydown', (event) => {
      if (!event.repeat) startHold(event);
    });
    button.addEventListener('keyup', (event) => {
      if (['Enter', ' '].includes(event.key)) cancelHold();
    });
    button.addEventListener('blur', cancelHold);

    form.addEventListener('reset', () => {
      cancelAnimationFrame(animationFrame);
      challenge.dataset.soAntibotVerified = 'false';
      challenge.classList.remove('is-holding', 'is-verified', 'is-error');
      button.removeAttribute('disabled');
      button.setAttribute('aria-pressed', 'false');
      if (label) label.textContent = 'Press and hold to verify';
      if (icon) icon.textContent = '●';
      setProgress(0);
    });

    challenge.dataset.soAntibotPrepared = 'true';
  };

  const prepareForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return;

    if (!form.dataset.soAntibotReadyAt) {
      form.dataset.soAntibotReadyAt = String(Date.now() + minimumCompletionTime);
      const marker = form.querySelector('[data-so-antibot-marker]');
      if (marker) marker.value = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    prepareHoldChallenge(form);
  };

  const prepareForms = (root = document) => {
    root.querySelectorAll('[data-so-antibot-marker]').forEach((marker) => prepareForm(marker.closest('form')));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => prepareForms(), { once: true });
  } else {
    prepareForms();
  }

  document.addEventListener('shopify:section:load', (event) => prepareForms(event.target));

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.querySelector('[data-so-antibot-marker]')) return;

      prepareForm(form);
      const honeypot = form.querySelector('[data-so-antibot-honeypot]');
      const challenge = form.querySelector('[data-so-antibot-challenge]');
      const isHoneypotFilled = Boolean(honeypot && honeypot.value.trim());
      const submittedTooQuickly = Date.now() < Number(form.dataset.soAntibotReadyAt || 0);
      const challengePassed = !challenge || challenge.dataset.soAntibotVerified === 'true';

      if (!isHoneypotFilled && !submittedTooQuickly && challengePassed) {
        hideMessage(form);
        challenge?.classList.remove('is-error');
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (isHoneypotFilled) {
        form.reset();
        return;
      }

      const message = form.querySelector('[data-so-antibot-message]');
      if (message) {
        if (!challengePassed) {
          message.textContent = 'Please complete the press-and-hold verification before submitting.';
        } else {
          message.textContent = 'Please wait a moment, then submit the form again.';
        }
        message.hidden = false;
        message.focus?.();
      }

      if (!challengePassed && challenge) {
        challenge.classList.add('is-error');
        challenge.querySelector('[data-so-antibot-hold]')?.focus();
      }
    },
    true,
  );
})();
